<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { PAGEVIEWS_EMPTY, partialPeriodFootnote } from '@/utils/emptyStateCopy.js';
import { formatExactPeriod } from '@/utils/timeWindows.js';

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const data = ref({ current: [], prior: [], partialPeriod: { active: false, since: null }, periods: null });

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.pageviews({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    data.value = {
      current: result.current ?? [],
      prior: result.prior ?? [],
      partialPeriod: result.partialPeriod ?? { active: false, since: null },
      periods: result.periods ?? null,
    };
    const totalCurrent = data.value.current.reduce((acc, b) => acc + b.value, 0);
    ps.setState(totalCurrent === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load pageviews",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

const chartLabels = computed(() => data.value.current.map((point) => point.ts));
const chartDatasets = computed(() => [
  {
    label: 'Current',
    data: data.value.current.map((point) => point.value),
    tooltipLabels: data.value.current.map((point) => point.ts),
  },
  {
    label: 'Prior',
    data: data.value.prior.map((point) => point.value),
    tooltipLabels: data.value.prior.map((point) => point.ts),
    borderDash: [4, 4],
  }
]);

const partialFootnote = computed(() => {
  if (!data.value.partialPeriod?.active) return null;
  const since = data.value.partialPeriod.since;
  if (!since) return null;
  // Compute relative — this could use Intl.RelativeTimeFormat for
  // tenant-locale awareness; MVP es básico ("X hours ago").
  const ms = Date.now() - new Date(since).getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return partialPeriodFootnote(`${hours} hours ago`);
  const days = Math.floor(hours / 24);
  return partialPeriodFootnote(`${days} days ago`);
});

const ariaLabel = computed(() => {
  const total = data.value.current.reduce((acc, b) => acc + b.value, 0);
  return `${total} pageviews in current window; comparison with prior period below`;
});
const periodsLabel = computed(() => data.value.periods
  ? `Current: ${formatExactPeriod(data.value.periods.current)}. Prior: ${formatExactPeriod(data.value.periods.prior)}. ${data.value.periods.timezone ?? ''}`
  : '');
</script>

<template>
  <PanelCard
    title="Pageviews"
    help-key="pageviewsPanel"
    :state="ps.state.value"
    :empty-headline="PAGEVIEWS_EMPTY.headline"
    :empty-body="PAGEVIEWS_EMPTY.body"
    :empty-action="PAGEVIEWS_EMPTY.action"
    empty-integration-guide
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <p
      v-if="periodsLabel"
      class="pageviews__periods"
    >
      {{ periodsLabel }}
    </p>
    <figure class="pageviews">
      <AnalyticsChart
        type="line"
        :labels="chartLabels"
        :datasets="chartDatasets"
        :aria-label="ariaLabel"
      />
      <figcaption
        v-if="partialFootnote"
        class="pageviews__footnote"
      >
        {{ partialFootnote }}
      </figcaption>
    </figure>
    <template
      v-if="data.current.length"
      #tableFallback
    >
      <table class="pv-table">
        <caption class="visually-hidden">
          Pageviews — current vs prior
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Time bucket
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Current
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Prior
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(b, i) in data.current"
            :key="`pv-${i}`"
          >
            <td>{{ b.ts }}</td>
            <td class="numeric">
              {{ b.value }}
            </td>
            <td class="numeric">
              {{ data.prior[i]?.value ?? '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.pageviews {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
}

.pageviews__periods {
  margin: 0 0 var(--space-2);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.pageviews__chart {
  width: 100%;
  height: 8rem;
}

.pageviews__path {
  stroke-width: var(--bw-thick);
  stroke-linecap: round;
  stroke-linejoin: round;
}

.pageviews__path--current {
  stroke: var(--chart-1);
}

.pageviews__path--prior {
  stroke: var(--chart-2);
  stroke-dasharray: var(--space-1) var(--space-1);
}

.pageviews__footnote {
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
}

.pv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.pv-table th,
.pv-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.pv-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.pv-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
